import { makeChoices, makeTextChoices } from "../distractors";
import { clean, frac, gcd, lcm, numStr, numUnit, simplify, text } from "../format";
import type { QuestionDraft, Rng, TopicDef } from "../types";
import { both, byTier, fraction, numeric, q } from "./helpers";

const DENS = [2, 3, 4, 5, 6, 8, 10, 12];
const CM2 = { pl: "cm²", en: "cm²" };
const CM3 = { pl: "cm³", en: "cm³" };
const cm2 = (n: number) => numUnit(n, "cm²");
const cm3 = (n: number) => numUnit(n, "cm³");

function fracChoices(rng: Rng, num: number, den: number, others: Array<[number, number]>) {
  const { num: n, den: d } = simplify(num, den);
  const correct = frac(n, d);
  const alts = others
    .map(([a, b]) => simplify(a, b))
    .filter((f) => f.den !== 0 && f.num > 0 && !(f.num === n && f.den === d))
    .map((f) => frac(f.num, f.den));
  // pad with fractions that are guaranteed to differ in value: (n+i)/(d+i+1) is never equal to n/d
  return makeTextChoices(rng, correct, alts, (i) => {
    const f = simplify(n + i + 1, d + i + 2);
    return frac(f.num, f.den);
  });
}

// ---------------------------------------------------------------- 1. fractions add / sub
function fractionsAddSub(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const d = rng.int(3, 12);
      const a = rng.int(1, d - 1);
      const b = rng.int(1, d - 1);
      const add = rng.chance(0.6) || a <= b;
      const num = add ? a + b : a - b;
      const s = simplify(num, d);
      const op = add ? "+" : "−";
      const choice4 = fracChoices(rng, num, d, [
        [num, 2 * d],
        [a * b, d],
        [num + 1, d],
      ]);
      return q(
        both(`Oblicz: ${a}/${d} ${op} ${b}/${d} = ?`, `Calculate: ${a}/${d} ${op} ${b}/${d} = ?`),
        fraction(s.num, s.den),
        [
          `Ten sam mianownik: ${add ? "dodajemy" : "odejmujemy"} liczniki: ${a} ${op} ${b} = ${num}.`,
          `Wynik: ${num}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `Same denominator: ${add ? "add" : "subtract"} the numerators: ${a} ${op} ${b} = ${num}.`,
          `Result: ${num}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
    () => {
      let d1 = rng.pick(DENS);
      let d2 = rng.pick(DENS);
      while (d2 === d1 || lcm(d1, d2) > 24) {
        d1 = rng.pick(DENS);
        d2 = rng.pick(DENS);
      }
      const a = rng.int(1, d1 - 1);
      const b = rng.int(1, d2 - 1);
      const L = lcm(d1, d2);
      const na = a * (L / d1);
      const nb = b * (L / d2);
      const add = rng.chance(0.6) || na <= nb;
      const num = add ? na + nb : na - nb;
      const s = simplify(num, L);
      const op = add ? "+" : "−";
      const choice4 = fracChoices(rng, num, L, [
        [add ? a + b : a - b, d1 + d2],
        [add ? a + b : Math.abs(a - b), Math.max(d1, d2)],
        [num + 1, L],
      ]);
      return q(
        both(`Oblicz: ${a}/${d1} ${op} ${b}/${d2} = ?`, `Calculate: ${a}/${d1} ${op} ${b}/${d2} = ?`),
        fraction(s.num, s.den),
        [
          `Wspólny mianownik: ${L}. ${a}/${d1} = ${na}/${L}, ${b}/${d2} = ${nb}/${L}.`,
          `${na}/${L} ${op} ${nb}/${L} = ${num}/${L}${s.den !== L ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `Common denominator: ${L}. ${a}/${d1} = ${na}/${L}, ${b}/${d2} = ${nb}/${L}.`,
          `${na}/${L} ${op} ${nb}/${L} = ${num}/${L}${s.den !== L ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const d = rng.int(3, 8);
      const w1 = rng.int(1, 4);
      const w2 = rng.int(1, 3);
      const a = rng.int(1, d - 1);
      const b = rng.int(1, d - 1);
      const total = (w1 + w2) * d + a + b;
      const s = simplify(total, d);
      const choice4 = fracChoices(rng, total, d, [
        [(w1 + w2) * d + a + b, 2 * d],
        [w1 * d + a + b, d],
        [total + d, d],
      ]);
      return q(
        both(
          `Oblicz: ${w1} ${a}/${d} + ${w2} ${b}/${d} = ? (podaj jako ułamek)`,
          `Calculate: ${w1} ${a}/${d} + ${w2} ${b}/${d} = ? (give a fraction)`,
        ),
        fraction(s.num, s.den),
        [
          `Całości: ${w1} + ${w2} = ${w1 + w2}. Ułamki: ${a}/${d} + ${b}/${d} = ${a + b}/${d}.`,
          `Razem: ${w1 + w2} + ${a + b}/${d} = ${total}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `Whole parts: ${w1} + ${w2} = ${w1 + w2}. Fractions: ${a}/${d} + ${b}/${d} = ${a + b}/${d}.`,
          `Together: ${w1 + w2} + ${a + b}/${d} = ${total}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 2. fractions multiply
function fractionsMult(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const d = rng.int(2, 9);
      const a = rng.int(1, d - 1);
      const n = rng.int(2, 9);
      const s = simplify(a * n, d);
      const choice4 = fracChoices(rng, a * n, d, [
        [a * n, d * n],
        [a + n, d],
        [a, d * n],
      ]);
      return q(
        both(`Oblicz: ${a}/${d} · ${n} = ?`, `Calculate: ${a}/${d} × ${n} = ?`),
        fraction(s.num, s.den),
        [
          `Mnożymy licznik przez liczbę: ${a} · ${n} = ${a * n}.`,
          `Wynik: ${a * n}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `Multiply the numerator by the number: ${a} × ${n} = ${a * n}.`,
          `Result: ${a * n}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const d1 = rng.int(2, 9);
      const d2 = rng.int(2, 9);
      const a = rng.int(1, d1 - 1);
      const b = rng.int(1, d2 - 1);
      const s = simplify(a * b, d1 * d2);
      const choice4 = fracChoices(rng, a * b, d1 * d2, [
        [a + b, d1 + d2],
        [a * b, d1 + d2],
        [a * d2 + b * d1, d1 * d2],
      ]);
      return q(
        both(`Oblicz: ${a}/${d1} · ${b}/${d2} = ?`, `Calculate: ${a}/${d1} × ${b}/${d2} = ?`),
        fraction(s.num, s.den),
        [
          `Licznik razy licznik, mianownik razy mianownik: ${a}·${b} / ${d1}·${d2}.`,
          `= ${a * b}/${d1 * d2}${s.den !== d1 * d2 ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `Numerator times numerator, denominator times denominator: ${a}·${b} / ${d1}·${d2}.`,
          `= ${a * b}/${d1 * d2}${s.den !== d1 * d2 ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const d = rng.pick([2, 3, 4, 5, 6, 8]);
      const a = rng.int(1, d - 1);
      const n = d * rng.int(2, 12);
      const value = (a * n) / d;
      const choice4 = makeChoices(rng, value, [n / d, value + a, n - a, a * n], (r) => r.int(1, 100));
      return q(
        both(`Oblicz ${a}/${d} z liczby ${n}.`, `Find ${a}/${d} of ${n}.`),
        numeric(value),
        [
          `Dzielimy przez mianownik: ${n} : ${d} = ${n / d}.`,
          `Mnożymy przez licznik: ${n / d} · ${a} = ${value}.`,
        ],
        [
          `Divide by the denominator: ${n} ÷ ${d} = ${n / d}.`,
          `Multiply by the numerator: ${n / d} × ${a} = ${value}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 3. decimals
function decimalsOps(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = clean(rng.int(10, 999) / 100);
      const b = clean(rng.int(10, 999) / 100);
      const add = rng.chance(0.5) || a < b;
      const value = clean(add ? a + b : a - b);
      const [x, y] = add || a >= b ? [a, b] : [b, a];
      const op = add ? "+" : "−";
      const choice4 = makeChoices(
        rng,
        value,
        [clean(add ? x - y : x + y), clean(value + 0.1), clean(value * 10)],
        (r) => clean(r.int(1, 2000) / 100),
      );
      return q(
        both(
          `Oblicz: ${numStr(x, "pl")} ${op} ${numStr(y, "pl")} = ?`,
          `Calculate: ${numStr(x, "en")} ${op} ${numStr(y, "en")} = ?`,
        ),
        numeric(value),
        [
          `Ustawiamy przecinek pod przecinkiem i ${add ? "dodajemy" : "odejmujemy"} jak liczby całkowite.`,
          `Wynik: ${numStr(value, "pl")}.`,
        ],
        [
          `Line up the decimal points and ${add ? "add" : "subtract"} like whole numbers.`,
          `Result: ${numStr(value, "en")}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = clean(rng.int(11, 99) / 10);
      const n = rng.int(2, 9);
      const value = clean(a * n);
      const choice4 = makeChoices(rng, value, [clean(a * n * 10), clean(a + n), clean(value + 1)], (r) =>
        clean(r.int(10, 900) / 10),
      );
      return q(
        both(`Oblicz: ${numStr(a, "pl")} · ${n} = ?`, `Calculate: ${numStr(a, "en")} × ${n} = ?`),
        numeric(value),
        [
          `Mnożymy bez przecinka: ${Math.round(a * 10)} · ${n} = ${Math.round(a * 10) * n}.`,
          `Oddzielamy jedną cyfrę po przecinku: ${numStr(value, "pl")}.`,
        ],
        [
          `Multiply without the point: ${Math.round(a * 10)} × ${n} = ${Math.round(a * 10) * n}.`,
          `Put one decimal place back: ${numStr(value, "en")}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = clean(rng.int(11, 99) / 10);
      const b = clean(rng.int(2, 9) / 10);
      const value = clean(a * b);
      const choice4 = makeChoices(rng, value, [clean(a * b * 10), clean(a + b), clean(value / 10)], (r) =>
        clean(r.int(1, 900) / 100),
      );
      return q(
        both(
          `Oblicz: ${numStr(a, "pl")} · ${numStr(b, "pl")} = ?`,
          `Calculate: ${numStr(a, "en")} × ${numStr(b, "en")} = ?`,
        ),
        numeric(value),
        [
          `Mnożymy bez przecinków: ${Math.round(a * 10)} · ${Math.round(b * 10)} = ${Math.round(a * 10) * Math.round(b * 10)}.`,
          `Razem 2 cyfry po przecinku: ${numStr(value, "pl")}.`,
        ],
        [
          `Multiply without the points: ${Math.round(a * 10)} × ${Math.round(b * 10)} = ${Math.round(a * 10) * Math.round(b * 10)}.`,
          `Two decimal places in total: ${numStr(value, "en")}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 4. divisibility
function divisibility(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  const d = byTier(
    tier,
    () => rng.pick([2, 5, 10]),
    () => rng.pick([3, 9]),
    () => rng.pick([4, 6]),
  );
  const list: number[] = [];
  while (list.length < 6) {
    const n = rng.int(12, 999);
    if (!list.includes(n)) list.push(n);
  }
  const hits = list.filter((n) => n % d === 0);
  const count = hits.length;
  const rulePl: Record<number, string> = {
    2: "ostatnia cyfra parzysta",
    5: "ostatnia cyfra 0 lub 5",
    10: "ostatnia cyfra 0",
    3: "suma cyfr podzielna przez 3",
    9: "suma cyfr podzielna przez 9",
    4: "liczba z dwóch ostatnich cyfr podzielna przez 4",
    6: "parzysta i suma cyfr podzielna przez 3",
  };
  const ruleEn: Record<number, string> = {
    2: "last digit even",
    5: "last digit 0 or 5",
    10: "last digit 0",
    3: "digit sum divisible by 3",
    9: "digit sum divisible by 9",
    4: "last two digits divisible by 4",
    6: "even and digit sum divisible by 3",
  };
  const choice4 = makeChoices(rng, count, [count + 1, count - 1, 6 - count, count + 2], (r) => r.int(0, 6));
  return q(
    both(
      `Ile liczb z listy ${list.join(", ")} jest podzielnych przez ${d}?`,
      `How many of the numbers ${list.join(", ")} are divisible by ${d}?`,
    ),
    numeric(count),
    [
      `Cecha podzielności przez ${d}: ${rulePl[d]}.`,
      hits.length
        ? `Podzielne: ${hits.join(", ")} — razem ${count}.`
        : `Żadna z liczb nie jest podzielna przez ${d}.`,
    ],
    [
      `Divisibility rule for ${d}: ${ruleEn[d]}.`,
      hits.length
        ? `Divisible: ${hits.join(", ")} — ${count} in total.`
        : `None of the numbers is divisible by ${d}.`,
    ],
    { choice4 },
  );
}

// ---------------------------------------------------------------- 5. primes
const PRIMES = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47];
const isPrime = (n: number) => n >= 2 && PRIMES.includes(n);

function primes(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.int(2, 38);
      const b = a + rng.int(6, 12);
      const found = [];
      for (let n = a; n <= b; n++) if (isPrime(n)) found.push(n);
      const choice4 = makeChoices(
        rng,
        found.length,
        [found.length + 1, found.length - 1, found.length + 2],
        (r) => r.int(0, 8),
      );
      return q(
        both(
          `Ile liczb pierwszych jest wśród liczb od ${a} do ${b}?`,
          `How many prime numbers are there from ${a} to ${b}?`,
        ),
        numeric(found.length),
        [
          `Liczba pierwsza ma dokładnie dwa dzielniki: 1 i siebie.`,
          `Pierwsze w tym zakresie: ${found.join(", ") || "brak"} — razem ${found.length}.`,
        ],
        [
          `A prime has exactly two divisors: 1 and itself.`,
          `Primes in this range: ${found.join(", ") || "none"} — ${found.length} in total.`,
        ],
        { choice4 },
      );
    },
    () => {
      const factors = Array.from({ length: rng.int(3, 4) }, () => rng.pick([2, 2, 3, 3, 5, 7]));
      const n = factors.reduce((a, b) => a * b, 1);
      const sorted = [...factors].sort((x, y) => x - y);
      const choice4 = makeChoices(
        rng,
        sorted.length,
        [
          sorted.length + 1,
          sorted.length - 1,
          new Set(sorted).size === sorted.length ? sorted.length + 2 : new Set(sorted).size,
        ],
        (r) => r.int(2, 6),
      );
      return q(
        both(
          `Rozłóż liczbę ${n} na czynniki pierwsze. Ile czynników ma rozkład (licząc powtórzenia)?`,
          `Factor ${n} into primes. How many prime factors are there (counting repeats)?`,
        ),
        numeric(sorted.length),
        [`${n} = ${sorted.join(" · ")}.`, `Czynników jest ${sorted.length}.`],
        [`${n} = ${sorted.join(" × ")}.`, `There are ${sorted.length} factors.`],
        { choice4 },
      );
    },
    () => {
      const p = rng.pick([2, 3, 5]);
      const r = rng.pick([7, 11, 13, 17, 19]);
      const mid = rng.pick([2, 3, 5, 7].filter((x) => x !== p && x < r));
      const n = p * mid * r;
      const choice4 = makeChoices(rng, r, [n, p, mid, r + 2], (x) => x.int(2, 30));
      return q(
        both(
          `Jaki jest największy dzielnik pierwszy liczby ${n}?`,
          `What is the largest prime divisor of ${n}?`,
        ),
        numeric(r),
        [
          `${n} = ${[p, mid, r].sort((a, b) => a - b).join(" · ")}.`,
          `Największy z czynników pierwszych: ${r}.`,
        ],
        [`${n} = ${[p, mid, r].sort((a, b) => a - b).join(" × ")}.`, `The largest prime factor: ${r}.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 6. GCD / LCM
function gcdLcm(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const g = rng.pick([2, 3, 4, 5, 6, 8, 9, 12]);
      const a = g * rng.int(2, 9);
      let b = g * rng.int(2, 9);
      while (a === b) b = g * rng.int(2, 9);
      const real = gcd(a, b);
      const choice4 = makeChoices(rng, real, [lcm(a, b), Math.min(a, b), real * 2, real / 2], (r) =>
        r.int(1, 30),
      );
      return q(
        both(`Oblicz NWD(${a}, ${b}).`, `Find GCD(${a}, ${b}).`),
        numeric(real),
        [
          `NWD to największa liczba, przez którą dzielą się obie liczby.`,
          `${a} = ${real} · ${a / real}, ${b} = ${real} · ${b / real}, więc NWD = ${real}.`,
        ],
        [
          `The GCD is the largest number dividing both.`,
          `${a} = ${real} × ${a / real}, ${b} = ${real} × ${b / real}, so GCD = ${real}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 12);
      let b = rng.int(2, 12);
      while (b === a) b = rng.int(2, 12);
      const L = lcm(a, b);
      const choice4 = makeChoices(rng, L, [a * b, gcd(a, b), L + a, Math.max(a, b)], (r) => r.int(2, 120));
      return q(
        both(`Oblicz NWW(${a}, ${b}).`, `Find LCM(${a}, ${b}).`),
        numeric(L),
        [
          `NWW to najmniejsza liczba, która dzieli się przez obie liczby.`,
          `Wielokrotności ${Math.max(a, b)}: ${[1, 2, 3, 4].map((k) => k * Math.max(a, b)).join(", ")}… Pierwsza podzielna przez ${Math.min(a, b)}: ${L}.`,
        ],
        [
          `The LCM is the smallest number divisible by both.`,
          `Multiples of ${Math.max(a, b)}: ${[1, 2, 3, 4].map((k) => k * Math.max(a, b)).join(", ")}… The first divisible by ${Math.min(a, b)}: ${L}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = rng.pick([4, 6, 8, 10, 12, 15]);
      let b = rng.pick([6, 9, 10, 12, 15, 20]);
      while (b === a) b = rng.pick([6, 9, 10, 12, 15, 20]);
      const L = lcm(a, b);
      const choice4 = makeChoices(rng, L, [a * b, a + b, gcd(a, b), L * 2], (r) => r.int(10, 120));
      return q(
        both(
          `Autobus do wioski odjeżdża co ${a} minut, a do kopalni co ${b} minut. O 8:00 odjechały razem. Po ilu minutach znów odjadą razem?`,
          `A bus to the village leaves every ${a} minutes, one to the mines every ${b} minutes. They left together at 8:00. After how many minutes do they leave together again?`,
        ),
        numeric(L, { pl: "min", en: "min" }),
        [
          `Szukamy najmniejszej wspólnej wielokrotności ${a} i ${b}.`,
          `NWW(${a}, ${b}) = ${L}, czyli po ${L} minutach.`,
        ],
        [
          `We need the least common multiple of ${a} and ${b}.`,
          `LCM(${a}, ${b}) = ${L}, so after ${L} minutes.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 7. percent intro
function percentIntro(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const p = rng.pick([50, 25, 10, 20, 75]);
      const n = rng.int(1, 12) * 20;
      const value = (n * p) / 100;
      const choice4 = makeChoices(rng, value, [n - value, p, (n * p) / 10], (r) => r.int(1, 200));
      const fracPl = {
        50: "połowa",
        25: "ćwierć",
        10: "jedna dziesiąta",
        20: "jedna piąta",
        75: "trzy czwarte",
      }[p];
      const fracEn = {
        50: "a half",
        25: "a quarter",
        10: "one tenth",
        20: "one fifth",
        75: "three quarters",
      }[p];
      return q(
        both(`Oblicz ${p}% z ${n}.`, `Find ${p}% of ${n}.`),
        numeric(value),
        [`${p}% to ${fracPl}.`, `${n} · ${p}/100 = ${value}.`],
        [`${p}% is ${fracEn}.`, `${n} × ${p}/100 = ${value}.`],
        { choice4 },
      );
    },
    () => {
      const [p, n, d] = rng.pick([
        [50, 1, 2],
        [25, 1, 4],
        [75, 3, 4],
        [10, 1, 10],
        [20, 1, 5],
        [40, 2, 5],
        [60, 3, 5],
        [30, 3, 10],
      ] as Array<[number, number, number]>);
      const choice4 = fracChoices(rng, n, d, [
        [p, 10],
        [n, d * 2],
        [n + 1, d],
      ]);
      return q(
        both(`Zapisz ${p}% jako ułamek zwykły nieskracalny.`, `Write ${p}% as a fraction in lowest terms.`),
        fraction(n, d),
        [`${p}% = ${p}/100.`, `Skracamy: ${p}/100 = ${n}/${d}.`],
        [`${p}% = ${p}/100.`, `Simplify: ${p}/100 = ${n}/${d}.`],
        { choice4 },
      );
    },
    () => {
      const total = rng.pick([20, 24, 25, 30, 40, 50]);
      const p = rng.pick([10, 20, 25, 50].filter((x) => (total * x) % 100 === 0));
      const value = (total * p) / 100;
      const choice4 = makeChoices(rng, value, [total - value, p, value * 2], (r) => r.int(1, 50));
      return q(
        both(
          `W klasie jest ${total} uczniów. ${p}% z nich gra w szachy. Ilu uczniów gra w szachy?`,
          `A class has ${total} pupils. ${p}% of them play chess. How many pupils play chess?`,
        ),
        numeric(value),
        [`${p}% z ${total} = ${total} · ${p}/100.`, `= ${value} uczniów.`],
        [`${p}% of ${total} = ${total} × ${p}/100.`, `= ${value} pupils.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 8. integers intro
function integersIntro(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const morning = -rng.int(1, 12);
      const noon = rng.int(1, 12);
      const diff = noon - morning;
      const choice4 = makeChoices(rng, diff, [noon + morning, noon, Math.abs(morning), diff + 1], (r) =>
        r.int(1, 30),
      );
      return q(
        both(
          `Rano było ${morning}°C, a w południe ${noon}°C. O ile stopni wzrosła temperatura?`,
          `In the morning it was ${morning}°C, at noon ${noon}°C. By how many degrees did the temperature rise?`,
        ),
        numeric(diff),
        [
          `Od ${morning} do 0 jest ${-morning} stopni, od 0 do ${noon} jest ${noon}.`,
          `Razem ${-morning} + ${noon} = ${diff}.`,
        ],
        [
          `From ${morning} to 0 is ${-morning} degrees, from 0 to ${noon} is ${noon}.`,
          `Together ${-morning} + ${noon} = ${diff}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = -rng.int(1, 20);
      let b = -rng.int(1, 20);
      while (b === a) b = -rng.int(1, 20);
      const smaller = Math.min(a, b);
      const choice4 = makeChoices(
        rng,
        smaller,
        [Math.max(a, b), -smaller, -Math.max(a, b)],
        (r) => -r.int(1, 25),
      );
      return q(
        both(
          `Która liczba jest mniejsza: ${a} czy ${b}? Wpisz ją.`,
          `Which number is smaller: ${a} or ${b}? Type it.`,
        ),
        numeric(smaller),
        [
          `Na osi liczbowej mniejsza jest ta bardziej na lewo.`,
          `${smaller} leży dalej od zera po lewej stronie, więc jest mniejsza.`,
        ],
        [
          `On the number line the smaller number is further left.`,
          `${smaller} is further from zero on the left, so it is smaller.`,
        ],
        { choice4 },
      );
    },
    () => {
      const start = -rng.int(1, 10);
      const drop = rng.int(2, 12);
      const value = start - drop;
      const choice4 = makeChoices(rng, value, [start + drop, -value, drop - start], (r) => -r.int(1, 25));
      return q(
        both(
          `Temperatura ${start}°C spadła o ${drop} stopni. Ile wynosi teraz?`,
          `The temperature ${start}°C dropped by ${drop} degrees. What is it now?`,
        ),
        numeric(value),
        [`Spadek to odejmowanie: ${start} − ${drop}.`, `Idziemy ${drop} w lewo na osi: ${value}°C.`],
        [
          `A drop means subtracting: ${start} − ${drop}.`,
          `Move ${drop} to the left on the number line: ${value}°C.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 9. areas of figures
function areaFigures(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.int(3, 14);
      const h = rng.int(2, 12) * (a % 2 === 0 ? 1 : 2);
      const area = (a * h) / 2;
      const choice4 = makeChoices(rng, area, [a * h, a + h, area * 2 + 1], (r) => r.int(4, 120), cm2);
      return q(
        both(
          `Trójkąt ma podstawę ${a} cm i wysokość ${h} cm. Jakie jest jego pole?`,
          `A triangle has base ${a} cm and height ${h} cm. What is its area?`,
        ),
        numeric(area, CM2),
        [`Pole trójkąta = (a · h) : 2.`, `(${a} · ${h}) : 2 = ${area} cm².`],
        [`Area of a triangle = (a × h) ÷ 2.`, `(${a} × ${h}) ÷ 2 = ${area} cm².`],
        { choice4, visual: { kind: "shape", data: { type: "triangle", a, h } } },
      );
    },
    () => {
      const a = rng.int(3, 14);
      const h = rng.int(2, 10);
      const area = a * h;
      const choice4 = makeChoices(rng, area, [area / 2, a + h, 2 * (a + h)], (r) => r.int(4, 140), cm2);
      return q(
        both(
          `Równoległobok ma podstawę ${a} cm i wysokość ${h} cm. Jakie jest jego pole?`,
          `A parallelogram has base ${a} cm and height ${h} cm. What is its area?`,
        ),
        numeric(area, CM2),
        [
          `Pole równoległoboku = a · h (bok razy wysokość opuszczona na ten bok).`,
          `${a} · ${h} = ${area} cm².`,
        ],
        [
          `Area of a parallelogram = a × h (side times the height onto that side).`,
          `${a} × ${h} = ${area} cm².`,
        ],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(4, 14);
      const b = rng.int(2, a - 1);
      const h = (a + b) % 2 === 0 ? rng.int(2, 9) : rng.int(1, 4) * 2;
      const area = ((a + b) * h) / 2;
      const choice4 = makeChoices(rng, area, [(a + b) * h, a * h, (a * b) / 2], (r) => r.int(4, 150), cm2);
      return q(
        both(
          `Trapez ma podstawy ${a} cm i ${b} cm oraz wysokość ${h} cm. Jakie jest jego pole?`,
          `A trapezoid has bases ${a} cm and ${b} cm and height ${h} cm. What is its area?`,
        ),
        numeric(area, CM2),
        [`Pole trapezu = (a + b) · h : 2.`, `(${a} + ${b}) · ${h} : 2 = ${a + b} · ${h} : 2 = ${area} cm².`],
        [
          `Area of a trapezoid = (a + b) × h ÷ 2.`,
          `(${a} + ${b}) × ${h} ÷ 2 = ${a + b} × ${h} ÷ 2 = ${area} cm².`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 10. volume
function volumeCuboid(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const c = rng.int(2, 9);
      const v = a * b * c;
      const choice4 = makeChoices(
        rng,
        v,
        [a * b + c, 2 * (a * b + b * c + a * c), a + b + c, v + a],
        (r) => r.int(8, 700),
        cm3,
      );
      return q(
        both(
          `Prostopadłościan ma krawędzie ${a} cm, ${b} cm i ${c} cm. Jaka jest jego objętość?`,
          `A cuboid has edges ${a} cm, ${b} cm and ${c} cm. What is its volume?`,
        ),
        numeric(v, CM3),
        [`Objętość = a · b · c.`, `${a} · ${b} · ${c} = ${v} cm³.`],
        [`Volume = a × b × c.`, `${a} × ${b} × ${c} = ${v} cm³.`],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 10);
      const v = a ** 3;
      const choice4 = makeChoices(rng, v, [a * a, 6 * a * a, 3 * a, v + a], (r) => r.int(8, 1000), cm3);
      return q(
        both(
          `Sześcian ma krawędź ${a} cm. Jaka jest jego objętość?`,
          `A cube has edge ${a} cm. What is its volume?`,
        ),
        numeric(v, CM3),
        [`Objętość sześcianu = a³ = a · a · a.`, `${a} · ${a} · ${a} = ${v} cm³.`],
        [`Volume of a cube = a³ = a × a × a.`, `${a} × ${a} × ${a} = ${v} cm³.`],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 6) * 10;
      const b = rng.int(2, 5) * 10;
      const c = rng.int(2, 5) * 10;
      const litres = (a * b * c) / 1000;
      const choice4 = makeChoices(
        rng,
        litres,
        [litres * 10, litres / 10, a * b * c],
        (r) => r.int(1, 200),
        (n) => numUnit(n, "l"),
      );
      return q(
        both(
          `Akwarium ma wymiary ${a} cm × ${b} cm × ${c} cm. Ile litrów wody mieści?`,
          `An aquarium measures ${a} cm × ${b} cm × ${c} cm. How many litres of water does it hold?`,
        ),
        numeric(litres, { pl: "l", en: "l" }),
        [
          `Objętość: ${a} · ${b} · ${c} = ${a * b * c} cm³.`,
          `1 litr = 1000 cm³, więc ${a * b * c} : 1000 = ${litres} l.`,
        ],
        [
          `Volume: ${a} × ${b} × ${c} = ${a * b * c} cm³.`,
          `1 litre = 1000 cm³, so ${a * b * c} ÷ 1000 = ${litres} l.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 11. powers intro
function powersIntro(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const n = rng.int(2, 15);
      const v = n * n;
      const choice4 = makeChoices(rng, v, [2 * n, v + n, (n + 1) * (n + 1)], (r) => r.int(4, 225));
      return q(
        both(`Oblicz: ${n}² = ?`, `Calculate: ${n}² = ?`),
        numeric(v),
        [`Kwadrat liczby to liczba pomnożona przez samą siebie.`, `${n} · ${n} = ${v}.`],
        [`The square of a number is the number times itself.`, `${n} × ${n} = ${v}.`],
        { choice4 },
      );
    },
    () => {
      if (rng.chance(0.4)) {
        const k = rng.int(2, 6);
        const v = 10 ** k;
        const choice4 = makeChoices(rng, v, [10 * k, 10 ** (k - 1), 10 ** (k + 1)], (r) => 10 ** r.int(1, 7));
        return q(
          both(`Oblicz: 10^${k} = ?`, `Calculate: 10^${k} = ?`),
          numeric(v),
          [`10^${k} to jedynka i ${k} zer.`, `= ${v}.`],
          [`10^${k} is a 1 followed by ${k} zeros.`, `= ${v}.`],
          { choice4 },
        );
      }
      const n = rng.int(2, 7);
      const v = n ** 3;
      const choice4 = makeChoices(rng, v, [3 * n, n * n, v + n], (r) => r.int(8, 400));
      return q(
        both(`Oblicz: ${n}³ = ?`, `Calculate: ${n}³ = ?`),
        numeric(v),
        [`Sześcian liczby: ${n} · ${n} · ${n}.`, `${n * n} · ${n} = ${v}.`],
        [`The cube of a number: ${n} × ${n} × ${n}.`, `${n * n} × ${n} = ${v}.`],
        { choice4 },
      );
    },
    () => {
      const n = rng.int(4, 15);
      const v = n * n;
      const choice4 = makeChoices(rng, n, [v / 2, n + 1, n - 1, v / 4], (r) => r.int(2, 20));
      return q(
        both(`Jaka liczba podniesiona do kwadratu daje ${v}?`, `Which number squared gives ${v}?`),
        numeric(n),
        [`Szukamy liczby, która pomnożona przez siebie daje ${v}.`, `${n} · ${n} = ${v}, więc to ${n}.`],
        [`We need the number that times itself gives ${v}.`, `${n} × ${n} = ${v}, so it is ${n}.`],
        { choice4 },
      );
    },
  );
}

const def = (
  id: string,
  pl: string,
  en: string,
  generate: (rng: Rng, tier: 1 | 2 | 3) => QuestionDraft,
): TopicDef => ({
  id,
  level: "sp5",
  name: text(pl, en),
  modes: ["numeric", "choice4"],
  generate,
});

export const SP5_TOPICS: readonly TopicDef[] = [
  def(
    "sp5.fractions_add_sub",
    "Dodawanie i odejmowanie ułamków",
    "Adding and subtracting fractions",
    fractionsAddSub,
  ),
  def("sp5.fractions_mult", "Mnożenie ułamków", "Multiplying fractions", fractionsMult),
  def("sp5.decimals_ops", "Działania na ułamkach dziesiętnych", "Operations on decimals", decimalsOps),
  def("sp5.divisibility", "Cechy podzielności", "Divisibility rules", divisibility),
  def("sp5.primes", "Liczby pierwsze", "Prime numbers", primes),
  def("sp5.gcd_lcm", "NWD i NWW", "GCD and LCM", gcdLcm),
  def("sp5.percent_intro", "Procenty – początki", "Percentages – basics", percentIntro),
  def("sp5.integers_intro", "Liczby ujemne", "Negative numbers", integersIntro),
  def("sp5.area_figures", "Pola figur", "Areas of figures", areaFigures),
  def("sp5.volume_cuboid", "Objętość prostopadłościanu", "Volume of a cuboid", volumeCuboid),
  def("sp5.powers_intro", "Potęgi – kwadraty i sześciany", "Powers – squares and cubes", powersIntro),
];
